import React, { useMemo, useState } from 'react'
import { Drawer, Button, Input } from 'antd'
import { BankOutlined, EyeOutlined, SearchOutlined } from '@ant-design/icons'
import styles from './AdministeredCompaniesDrawer.module.scss'

interface Props {
  userName: string
  companies: string[]
  open: boolean
  onClose: () => void
}

export default function AdministeredCompaniesDrawer({
  userName,
  companies,
  open,
  onClose,
}: Props) {
  const [search, setSearch] = useState('')

  const results = useMemo(() => {
    const query = search.trim().toLowerCase()
    if (query.length === 0) return companies
    return companies.filter((c) => c.toLowerCase().includes(query))
  }, [companies, search])

  const total = companies.length
  const companyWord = total === 1 ? 'company' : 'companies'

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={480}
      closable
      title={
        <div className={styles.header}>
          <span className={styles.eyebrowRow}>
            <span className={styles.eyebrow}>Administered companies</span>
            <span className={styles.viewOnly}>
              <EyeOutlined />
              View only
            </span>
          </span>
          <span className={styles.userName}>{userName}</span>
          <span className={styles.subtext}>
            Administers <strong>{total}</strong> {companyWord}
          </span>
        </div>
      }
      footer={
        <div className={styles.footer}>
          <span className={styles.footerNote}>
            Managed in the source system — not editable here.
          </span>
          <Button onClick={onClose}>Close</Button>
        </div>
      }
    >
      <Input
        placeholder="Search companies…"
        prefix={<SearchOutlined style={{ color: '#9ca3af' }} />}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        allowClear
        className={styles.search}
      />
      {results.length > 0 ? (
        <div className={styles.list}>
          {results.map((name, index) => (
            <div key={`${index}-${name}`} className={styles.row}>
              <BankOutlined className={styles.rowIcon} />
              <span className={styles.rowName}>{name}</span>
            </div>
          ))}
        </div>
      ) : companies.length === 0 ? (
        <div className={styles.empty}>
          This user does not administer any companies.
        </div>
      ) : (
        <div className={styles.empty}>No companies match “{search}”.</div>
      )}
    </Drawer>
  )
}
